import axios from "axios";

/**
 * Resolve the backend base URL.
 *
 * Build-time `REACT_APP_BACKEND_URL` is the source of truth, but Emergent
 * deploys can ship with a non-custom-domain URL baked in (e.g. preview
 * `*.emergentagent.com` or staging `*.emergent.host`). When that happens
 * the frontend talks cross-origin and CORS breaks every authenticated
 * request because the platform CDN injects `Access-Control-Allow-Origin: *`
 * which combined with `credentials: true` is a CORS spec violation.
 *
 * Self-heal: if we're running on a custom domain AND the baked URL points
 * at any Emergent-platform host (`emergentagent.com` OR `emergent.host`),
 * ignore the baked URL and use the current origin. Same-origin = no CORS =
 * cookie sticks.
 */
function resolveBaseUrl() {
  const baked = process.env.REACT_APP_BACKEND_URL || "";
  if (typeof window === "undefined") return baked;
  const host = window.location.hostname || "";
  const onEmergentPlatform =
    host.endsWith("emergentagent.com") ||
    host.endsWith("emergent.host") ||
    host === "localhost";
  const bakedIsEmergentPlatform = /(emergentagent\.com|emergent\.host)/i.test(baked);
  if (host && !onEmergentPlatform && bakedIsEmergentPlatform) {
    return window.location.origin;
  }
  return baked;
}

const BASE_URL = resolveBaseUrl();
export const API = `${BASE_URL}/api`;

/**
 * Session auth lives in an HTTP-only cookie (`mp_session`) set by the backend,
 * so the browser handles it automatically. `withCredentials: true` tells axios
 * to include that cookie on every request. We never touch it from JS — that is
 * the whole point: no localStorage token = no XSS token theft.
 */
export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

/** Routes a signed-out visitor can legitimately stay on. */
const PUBLIC_PATHS = [
  "/",
  "/login",
  "/register",
  "/forgot-password",
  "/reset-password",
  "/verify-email",
  "/pricing",
  "/leaderboard",
  "/martingale-method",
  "/arbitrage-betting",
  "/about",
  "/contact",
  "/terms",
  "/privacy",
  "/responsible-gambling",
];

function isPublicPath(path) {
  if (path === "/") return true;
  return PUBLIC_PATHS.some((p) => p !== "/" && path.startsWith(p));
}

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      // The /auth/me probe 401s on every public page load — that's expected.
      // Never force-redirect for it; the AuthContext handles the signed-out state.
      const reqUrl = err?.config?.url || "";
      const isAuthProbe = reqUrl.endsWith("/auth/me");
      if (!isAuthProbe && !isPublicPath(window.location.pathname)) {
        window.location.href = "/login";
      }
    }
    return Promise.reject(err);
  }
);
